<script setup lang="ts" generic="Value extends PickerValue = Dayjs">
import { ref, shallowRef } from 'vue'
import type { Dayjs } from 'dayjs'
import SinglePicker from './SinglePicker.vue'
import { useBoundValue } from '../../composables/useBoundValue'
import type { DatePickerProps } from './types'
import type { PickerValue } from './dateValue'

const props = withDefaults(defineProps<DatePickerProps<Value>>(), { disabled: undefined, inputReadOnly: undefined, allowClear: undefined, open: undefined, defaultOpen: undefined, showTime: undefined, showToday: undefined, showNow: undefined })
const emit = defineEmits<{
  'update:value': [value: Value | null]
  'update:open': [open: boolean]
  change: [value: Value | null, dateString: string]
  openChange: [open: boolean]
  panelChange: [value: Dayjs, mode: 'date' | 'month' | 'year']
  ok: [value: Value | null]
  focus: [event: FocusEvent]
  blur: [event: FocusEvent]
}>()
const innerValue = shallowRef(props.defaultValue ?? null)
const value = useBoundValue(props, 'value', innerValue)
const innerOpen = ref(props.defaultOpen ?? false)
const open = useBoundValue(props, 'open', innerOpen)
const pickerRef = ref<{ focus: (options?: FocusOptions) => void; blur: () => void } | null>(null)
function updateValue(next: Value | null) { innerValue.value = next; emit('update:value', next) }
function updateOpen(next: boolean) { innerOpen.value = next; emit('update:open', next) }
defineExpose({ focus: (options?: FocusOptions) => pickerRef.value?.focus(options), blur: () => pickerRef.value?.blur() })
</script>

<template>
  <SinglePicker ref="pickerRef" v-bind="props" :value="value" :open="open" @update:value="updateValue" @update:open="updateOpen" @change="(value, text) => emit('change', value, text)" @open-change="emit('openChange', $event)" @panel-change="(value, mode) => emit('panelChange', value, mode)" @ok="emit('ok', $event)" @focus="emit('focus', $event)" @blur="emit('blur', $event)">
    <template v-for="(_, name) in $slots" #[name]="slotProps"><slot :name="name" v-bind="slotProps ?? {}" /></template>
  </SinglePicker>
</template>
